import {Injectable} from '@angular/core';
import moment from 'moment-timezone';
import {
    FieldUiDataAssignmentObj,
    FieldUiDataComboBoxObj,
    FieldUiDataDateObj,
    FieldUiDataDateRangeObj,
    FieldUiDataNumberObj,
    FieldUiDataObj,
    FieldUiDataSetObj,
    KeyValueObj,
    ReportFieldMxObj,
    ReportUiDataObj,
    TypedObj
} from '../../../../generated/generated-core.model';

// See com.playtech.mexos.data.ReportFieldType
const NUMERIC_REPORT_FIELD_TYPES = ['long', 'number', 'percent'];
const DATE_RANGE_REPORT_INPUT_FIELD_TYPES = ['dateRange', 'comparativeDateRange'];
const SET_INPUT_FIELD_TYPE = 'set';

@Injectable({providedIn: 'root'})
export class MxReportFieldsConverterService {

    public static convertToEntity(field: ReportFieldMxObj): ReportUiDataObj {
        const sublistFn = value => MxReportFieldsConverterService.convertToEntity(value);
        return {
            field: field.name,
            label: field.label,
            type: field.type,
            required: field.required,
            hidden: null,
            value: field.value,
            values: field.values.map(dataBean => [dataBean.id, dataBean.name]),
            sublist: field.sublist.map(value => MxReportFieldsConverterService.convertToEntity(value)),
            collapsible: null,
            collapsed: field.collapsed,
            disabled: null,
            basedOnField: null,
            showIf: null,
            createColumn: null,
            dateTimeFormat: field.dateTimeFormat,
            referencedReport: null,
            referencedEntity: null,
            filter: field.filter,
            precision: field.precision,
            returnedColumn: null,
            isListWithParams: null,
            selectedIds: null,
            sortable: false,
            createSummary: false
        };
    }

    public convertToUiData(field: ReportUiDataObj): FieldUiDataObj {
        // TODO check percent type
        switch (field.type) {
            case 'text': return this.commonField(field);
            case 'list': return this.dropdownField(field);
            case 'boolean': return this.dropdownField(field);
            case 'number': return this.numberField(field);
            case 'long': return this.longField(field);
            case 'date': return this.dateField(field);
            case 'set': return this.setField(field);
            case 'dateRange': return this.dateRangeField(field);
            case 'dateTime': return this.dateTimeField(field);
            case 'comparativeDateRange': return this.dateRangeField(field);
            case 'assignmentgrid': return this.assignmentField(field);
            default: return this.commonField(field);
        }
    }

    private dropdownField(value: ReportUiDataObj): FieldUiDataComboBoxObj {
        let format = (item) => item;
        if (value.dateTimeFormat) {
            format = (item) => {
                let date: moment.Moment = moment(item);
                return date.format(value.dateTimeFormat.toUpperCase());
            };
        }
        let options: KeyValueObj[];
        if (value.values) {
            options = value.values.map(value1 => {
                return {key: value1[0], value: format(value1[1]), dependOn: value1[2]};
            });
        }
        let defaultValue = value.value ? value.value.toString() : value.value;
        if (options && options.findIndex(item => item.key === defaultValue) === -1) {
            defaultValue = undefined;
        }
        return {
            ...this.commonField(value),
            type: 'dropdown',
            defaultValue,
            options
        };
    }

    private numberField(value: ReportUiDataObj): FieldUiDataNumberObj {
        return {...this.commonField(value), type: 'numeric', precision: 3, positiveInteger: false };
    }

    private longField(value: ReportUiDataObj): FieldUiDataNumberObj {
        return {...this.commonField(value), type: 'numeric', precision: 0, positiveInteger: true };
    }

    private setField(value: ReportUiDataObj): FieldUiDataSetObj {
        return {
            ...this.commonField(value),
            type: 'fieldset',
            collapsible: value.collapsible,
            collapsed: value.collapsed,
            sublist: value.sublist.map(subVal => {
                return this.convertToUiData(subVal);
            })
        };
    }

    private dateField(value: ReportUiDataObj): FieldUiDataDateObj {
        return {...this.commonField(value), type: 'datepicker', minDate: null, maxDate: null};
    }

    private dateTimeField(value: ReportUiDataObj): FieldUiDataDateObj {
        return {...this.commonField(value), type: 'datetime', minDate: null, maxDate: null};
    }

    private dateRangeField(value: ReportUiDataObj): FieldUiDataDateRangeObj {
        return {
            ...this.commonField(value), type: 'daterange',
            validation: {
                maxLength: null,
                maxValue: null,
                minLength: null,
                minValue: null,
                pattern: null,
                required: false,
                type: null,
                integerDigits: null,
                fractionDigits: null,
                defaultValue: null
            },
            sublist: value.sublist.map(subVal => {
                return this.convertToUiData(subVal);
            }),
            fromDate: null, toDate: null
        };
    }

    public isDateRange(fieldOrType: TypedObj | string): boolean {
        return DATE_RANGE_REPORT_INPUT_FIELD_TYPES.indexOf(this.getType(fieldOrType)) >= 0;
    }

    public isNumeric(fieldOrType: TypedObj | string): boolean {
        return NUMERIC_REPORT_FIELD_TYPES.indexOf(this.getType(fieldOrType)) >= 0;
    }

    private getType(fieldOrType: TypedObj | string): string {
        let type;
        if ((<TypedObj> fieldOrType).type) {
            type = (<TypedObj>fieldOrType).type;
        } else {
            type = fieldOrType;
        }
        return type;
    }

    public getNumericFields(reportFields: Array<ReportUiDataObj>): Array<ReportUiDataObj> {
        return reportFields.filter((reportField) => this.isNumeric(reportField));
    }

    public flatSublists(reportFields: Array<ReportUiDataObj>): Array<ReportUiDataObj> {
        if (!reportFields) {
            return new Array<ReportUiDataObj>();
        } else {
            return reportFields.reduce((acc, val) => {
                if (val.type === SET_INPUT_FIELD_TYPE) {
                    return acc.concat(this.flatSublists(val.sublist));
                }
                return acc.concat(val);
            }, new Array<ReportUiDataObj>());
        }
    }

    private assignmentField(value: ReportUiDataObj): FieldUiDataAssignmentObj {
        return {
            ...this.commonField(value),
            type: 'assignmentgrid',
            assignmentComponent: value.field,
            returnedColumn: value.returnedColumn,
            selectionMode: 'multi',
            filter: value.filter
        };
    }

    private commonField(value: ReportUiDataObj): FieldUiDataObj {
        return {
            name: value.field,
            hidden: value.hidden,
            label: value.label,
            type: 'textbox',
            disabledOnEdit: null,
            disabledOnEditValues: null,
            disabled: value.disabled,
            disabledDependsOnField: value.basedOnField,
            labelCode: null,
            dependsOn: null,
            sublist: null,
            defaultValue: value.value,
            showIf: value.showIf,
            showChildDependOn: null,
            validation: {
                maxLength: null,
                maxValue: null,
                minLength: null,
                minValue: null,
                pattern: null,
                required: value.required,
                type: null,
                integerDigits: null,
                fractionDigits: null,
                defaultValue: null
            },
            showInCompactMode: true,
            informationKey: null
        };
    }


}
